
import { useCallback, useRef } from 'react';
import { JobSearchResult, ProcessingDetails } from '@/types/jobSearch';
import { supabase } from '@/integrations/supabase/client';

const FRONTEND_POLLING_INTERVAL = 10000; // 10 seconds
const MAX_FRONTEND_POLLING_DURATION = 5 * 60 * 1000; // 5 minutes

interface UseLutraPollingProps {
  setSearchResults: (results: JobSearchResult[]) => void;
  setError: (error: string | null) => void;
  setIsLoading: (loading: boolean) => void;
  setProcessingDetails: (details: ProcessingDetails | null) => void;
}

export const useLutraPolling = ({
  setSearchResults,
  setError,
  setIsLoading,
  setProcessingDetails,
}: UseLutraPollingProps) => {
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const frontendPollingStartTimeRef = useRef<number | null>(null);
  const currentProviderIdRef = useRef<string | null>(null);

  const clearPolling = useCallback(() => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    frontendPollingStartTimeRef.current = null;
    currentProviderIdRef.current = null;
  }, []);

  const startPolling = useCallback(async (pollUrl: string, providerId: string) => {
    console.log('[useLutraPolling] Starting polling for URL:', pollUrl);
    
    frontendPollingStartTimeRef.current = Date.now();
    currentProviderIdRef.current = providerId;
    
    const poll = async () => {
      try {
        // Check if we've exceeded the maximum polling duration
        if (frontendPollingStartTimeRef.current && 
            Date.now() - frontendPollingStartTimeRef.current > MAX_FRONTEND_POLLING_DURATION) {
          console.log('[useLutraPolling] Polling timeout reached');
          setError('Search request timed out. Please try again.');
          setIsLoading(false);
          setProcessingDetails(null);
          clearPolling();
          return;
        }

        console.log('[useLutraPolling] Polling Lutra results...');
        
        // Get current session for authentication
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          setError('Authentication session expired. Please log in again.');
          setIsLoading(false);
          setProcessingDetails(null);
          clearPolling();
          return;
        }

        const { data, error } = await supabase.functions.invoke('lutra-search-agent', {
          body: { 
            action: 'poll_results', 
            pollUrl,
            providerId: currentProviderIdRef.current 
          },
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        });

        if (error) {
          console.error('[useLutraPolling] Polling error:', error);
          setError(`Polling failed: ${error.message}`);
          setIsLoading(false);
          setProcessingDetails(null);
          clearPolling();
          return;
        }

        if (data?.results) {
          console.log('[useLutraPolling] Got results:', data.results.length, 'jobs');
          setSearchResults(data.results);
          setProcessingDetails(null);
          setIsLoading(false);
          clearPolling();
        } else if (data?.error) {
          console.error('[useLutraPolling] API error:', data.error);
          setError(data.error);
          setIsLoading(false);
          setProcessingDetails(null);
          clearPolling();
        } else {
          // Continue polling
          console.log('[useLutraPolling] Still processing, continuing to poll...');
          if (data?.processingDetails) {
            setProcessingDetails(data.processingDetails);
          }
          
          pollingTimeoutRef.current = setTimeout(poll, FRONTEND_POLLING_INTERVAL);
        }
      } catch (e: any) {
        console.error('[useLutraPolling] Polling exception:', e);
        setError(`Polling error: ${e.message}`);
        setIsLoading(false);
        setProcessingDetails(null);
        clearPolling();
      }
    };

    // Start the first poll immediately
    poll();
  }, [setSearchResults, setError, setIsLoading, setProcessingDetails, clearPolling]);

  return { startPolling, clearPolling };
};
